import { uniqueId } from 'lodash'
import React, { FC, Fragment, ReactNode, useState } from 'react'

import JournalActions from '../JournalActions/JournalActions'
import JournalCardVegetableAndFruitTracker from '../JournalCardVegetableAndFruitTracker/JournalCardVegetableAndFruitTracker'
import JournalHeader from '../JournalHeader/JournalHeader'
import MenuPlanTitle from '../MenuPlanTitle/MenuPlanTitle'
import FoodModal from '../Modals/FoodModal/FoodModal'
import RecipeModal from '../Modals/RecipeModal/RecipeModal'
import { JournalCardNotes } from '../Notes/JournalCardNotes'
import PlanSectionTitle from '../PlanSectionTitle/PlanSectionTitle'

import styles from './JournalDay.module.css'

import { Cockpit, CockpitProps, InfoPlaceholder, LoadingIndicator, MacronutrientCard } from 'Components'
import { DebugPanel } from 'Components/Debug'
import {
  CategoryGroupedJournalEatableContainerMapping,
  CategoryJournalEatableContainerMapping,
  FitnessPlanForDate,
  FitnessPlanSlot,
  JournalEatableContainerObject,
  JournalEnergyMotionRecord,
  JournalEnergyRecord,
  JournalMotionRecord,
  MealCategory,
  QuantifiedEatable,
  QuantifiedFood,
  QuantifiedRecipe
} from 'Models'
import JournalCardEatableContainer from 'Pages/JournalPage/components/JournalCardEatableContainer/JournalCardEatableContainer'
import JournalCardEnergyContainer from 'Pages/JournalPage/components/JournalCardEnergyContainer'
import JournalCardEnergyMotionContainer from 'Pages/JournalPage/components/JournalCardEnergyMotionContainer'
import JournalCardHydrationContainer from 'Pages/JournalPage/components/JournalCardHydrationContainer/JournalCardHydrationContainer'
import JournalCardMotionContainer from 'Pages/JournalPage/components/JournalCardMotionContainer'
import { getFlattenedMenuPlanContainers } from 'Pages/JournalPage/utils/journalEatableCardContainersForDate'
import sortJournalRecords from 'Pages/JournalPage/utils/sortJournalRecords'
import { useAppSelector } from 'ReduxStore/hooks'
import { userSettingsDataSelector } from 'ReduxStore/userSettings'
import { Calculator, Formatter } from 'Utils'

type Props = {
  journalEatableRecordContainers: CategoryJournalEatableContainerMapping
  menuPlanContainers: CategoryGroupedJournalEatableContainerMapping
  flattenedJournalEatableRecordsContainers: JournalEatableContainerObject[]
  journalEnergyMotionRecords: JournalEnergyMotionRecord[]
  journalEnergyRecords: JournalEnergyRecord[]
  journalMotionRecords: JournalMotionRecord[]
  fitnessPlansForDate: FitnessPlanForDate[]
  weight: number
  acceptedEnergyMotionSum?: number
  cockpitProps: CockpitProps
  showJournalCardsLoading: boolean
}

const JournalDay: FC<Props> = ({
  journalEatableRecordContainers,
  menuPlanContainers,
  flattenedJournalEatableRecordsContainers,
  journalEnergyMotionRecords,
  journalEnergyRecords,
  journalMotionRecords,
  fitnessPlansForDate,
  weight,
  acceptedEnergyMotionSum,
  cockpitProps,
  showJournalCardsLoading
}: Props) => {
  const [detailRecipe, setDetailRecipe] = useState<QuantifiedRecipe>()
  const [detailFood, setDetailFood] = useState<QuantifiedFood>()

  const userSettings = useAppSelector(userSettingsDataSelector)

  const menuPlanHidden = !!userSettings.menuPlanHidden

  const hydrationHidden = !!userSettings.hydrationHidden

  const vegetablesAndFruitsHidden = !!userSettings.vegetablesAndFruitsHidden

  const macronutrientsHidden = !!userSettings.macronutrientsHidden

  const notesHidden = !!userSettings.notesHidden

  const fitnessPlanSlotIds = fitnessPlansForDate.flatMap((plan) => plan.slots.map((slot) => slot.id))

  const motionRecordsNotInFitnessPlan = journalMotionRecords.filter((record) =>
    record.source ? !fitnessPlanSlotIds.includes(record.source.split(':')[1]) : true
  )

  const openRecipeDetails = (quantifiedEatable: QuantifiedEatable): void => {
    const { recipe, quantity, unit } = quantifiedEatable
    setDetailRecipe({ recipe, quantity, unit })
  }

  const closeRecipeDetails = (): void => {
    setDetailRecipe(undefined)
  }

  const renderRecipeModal = (): JSX.Element => {
    return <RecipeModal quantifiedRecipe={detailRecipe as QuantifiedRecipe} onClose={closeRecipeDetails} />
  }

  const openFoodDetails = (quantifiedEatable: QuantifiedEatable): void => {
    const { food, quantity, unit } = quantifiedEatable
    setDetailFood({ food, quantity, unit })
  }

  const closeFoodDetails = (): void => {
    setDetailFood(undefined)
  }

  const renderFoodModal = (): JSX.Element => {
    return <FoodModal quantifiedFood={detailFood as QuantifiedFood} onClose={closeFoodDetails} />
  }

  const renderEatableContainer = (
    {
      eatableCardObjects,
      eatableCardsActiveIndex,
      journalRecordId,
      menuPlanSlotId,
      source
    }: JournalEatableContainerObject,
    mealCategory: MealCategory
  ): ReactNode => {
    if (!eatableCardObjects || eatableCardObjects.length === 0) {
      return (
        <DebugPanel>
          empty eatable: <code>{JSON.stringify(eatableCardObjects)}</code>
        </DebugPanel>
      )
    }

    return (
      <JournalCardEatableContainer
        eatableCardObjects={eatableCardObjects}
        eatableCardsActiveIndex={eatableCardsActiveIndex}
        mealCategory={mealCategory}
        journalRecordId={journalRecordId}
        menuPlanSlotId={menuPlanSlotId}
        source={source}
        onCardClick={(eatable) =>
          eatable.recipe ? openRecipeDetails(eatable) : eatable.food && openFoodDetails(eatable)
        }
      />
    )
  }

  const renderJournalEatableCards = (
    eatableCardObjects: JournalEatableContainerObject[],
    category: MealCategory
  ): ReactNode =>
    eatableCardObjects.map((cardObject) => {
      if (menuPlanHidden && !cardObject.journalRecordId) {
        return null
      }
      return (
        <Fragment key={cardObject.journalRecordId || cardObject.menuPlanSlotId}>
          {renderEatableContainer(cardObject, category)}
        </Fragment>
      )
    })

  const renderJournalEnergyCards = (records: JournalEnergyRecord[]): ReactNode =>
    records.map((record: JournalEnergyRecord) => (
      <JournalCardEnergyContainer key={record.id} journalEnergyRecord={record} />
    ))

  const flattenedMenuPlanContainers = getFlattenedMenuPlanContainers({ menuPlanContainers: menuPlanContainers })

  const renderCategoryCards = (category: MealCategory): JSX.Element | null => {
    const energyRecordsForCategory = journalEnergyRecords.filter((record) => record.mealCategory === category)

    const eatableCardsForCategory = flattenedJournalEatableRecordsContainers.filter(
      (card) => card.mealCategory === category && !(menuPlanHidden && !card.journalRecordId)
    )

    const menuPlanCardsForCategory = flattenedMenuPlanContainers.filter(
      (card) => card.mealCategory === category && !(menuPlanHidden && !card.journalRecordId)
    )
    const isEmpty =
      eatableCardsForCategory.length === 0 &&
      energyRecordsForCategory.length === 0 &&
      menuPlanCardsForCategory.length === 0

    const menuPlanJournalRecordIds = menuPlanContainers[category]
      .map((group) => group.filter((slot) => slot.journalRecordId).map((slot) => slot.journalRecordId))
      .flat()

    const journalEatableCardContainersWithoutMenuPlanEntries = journalEatableRecordContainers[category].filter(
      (journalRecord) => !menuPlanJournalRecordIds.includes(journalRecord.journalRecordId)
    )

    const sumEnergyCards = Calculator.sumEnergy(energyRecordsForCategory)
    const sumEatableCards = Calculator.sumEnergyFromEatableContainerObjects(
      eatableCardsForCategory.filter((card) => card.journalRecordId)
    )

    const menuPlanGroupsForCategory = menuPlanContainers[category]

    const menuPlanGroupsToRender = menuPlanHidden
      ? menuPlanGroupsForCategory
          .map((cards) => cards.filter((card) => card.journalRecordId))
          .filter((cards) => cards.length > 0)
      : menuPlanGroupsForCategory

    const showMenuPlanSection = menuPlanGroupsToRender.length > 0

    const renderDivider = (index: number, groupCount: number): ReactNode => {
      if (index === groupCount - 1) {
        return null
      }

      return <hr className={styles.menuPlanDivider} />
    }

    const menuPlanMarginBottom =
      journalEatableCardContainersWithoutMenuPlanEntries.length === 0 && energyRecordsForCategory.length === 0
        ? ''
        : 'mb-2'

    return !isEmpty ? (
      <>
        <div className="standardcard-sectiontitle mt-2 mb-1">
          <span>{Formatter.formatMealCategory(category)}</span>
          <span>{Formatter.formatEnergy(sumEnergyCards + sumEatableCards)}</span>
        </div>
        <div className="row">
          <div className="col-12">
            {showMenuPlanSection && (
              <div className={`col-auto basiccard px-2 pb-2 pt-1 ${menuPlanMarginBottom}`}>
                <MenuPlanTitle menuPlanHidden={menuPlanHidden} />

                {menuPlanGroupsToRender.map((cards, index) => {
                  return (
                    <Fragment key={`slot:${cards[0].menuPlanSlotId}`}>
                      {renderJournalEatableCards(cards, category)}
                      {renderDivider(index, menuPlanGroupsToRender.length)}
                    </Fragment>
                  )
                })}
              </div>
            )}

            {renderJournalEatableCards(journalEatableCardContainersWithoutMenuPlanEntries, category)}
            {renderJournalEnergyCards(energyRecordsForCategory)}
          </div>
        </div>
      </>
    ) : null
  }

  const renderFitnessPlanSlot = (slot: FitnessPlanSlot): ReactNode => {
    const mappedSlot = {
      ...slot,
      motionLevelId: slot.motionLevel.id,
      motion: slot.motionLevel.motion
    }

    const fittingRecord = journalMotionRecords.find((record) => record.source?.split(':')[1] === slot.id)

    if (fittingRecord) {
      return (
        <JournalCardMotionContainer
          key={fittingRecord.id}
          journalMotionRecord={fittingRecord}
          isInFitnessPlan
          isAccepted
          weight={weight}
        />
      )
    }

    return (
      <JournalCardMotionContainer
        key={slot.id}
        journalMotionRecord={mappedSlot}
        isInFitnessPlan={true}
        weight={weight}
      />
    )
  }

  const renderMotionCards = (): ReactNode => {
    const hasFitnessPlanSlots = fitnessPlansForDate.length > 0

    return journalMotionRecords.length > 0 || journalEnergyMotionRecords.length > 0 || hasFitnessPlanSlots ? (
      <>
        <div className="standardcard-sectiontitle mt-2 mb-1">
          <span>Aktivitäten</span>
          <span>{Formatter.formatEnergy(acceptedEnergyMotionSum, true)}</span>
        </div>
        <div className="row">
          <div className="col-12">
            {fitnessPlansForDate.map((plan) => (
              <div key={plan.id} className="col-auto basiccard px-2 pb-2 pt-1 mb-2">
                <PlanSectionTitle
                  title={plan.displayName ? `Fitnessplan: ${plan.displayName}` : 'Fitnessplan'}
                  variant="fitness"
                />

                {plan.slots.map((slot, index) => (
                  <Fragment key={slot.id}>{renderFitnessPlanSlot(slot)}</Fragment>
                ))}
              </div>
            ))}

            {sortJournalRecords(motionRecordsNotInFitnessPlan).map((record) => (
              <JournalCardMotionContainer key={record.id} journalMotionRecord={record} weight={weight} />
            ))}
            {sortJournalRecords(journalEnergyMotionRecords).map((record: JournalEnergyMotionRecord) => (
              <JournalCardEnergyMotionContainer key={record.id} journalEnergyMotionRecord={record} />
            ))}
          </div>
        </div>
      </>
    ) : null
  }

  const renderJournalCards = (): JSX.Element => {
    if (showJournalCardsLoading) {
      return (
        <div className="text-center py-4">
          <LoadingIndicator />
          <p className="mt-2 mb-0">Einträge werden geladen …</p>
        </div>
      )
    }

    const breakfastCards = renderCategoryCards('BREAKFAST')
    const lunchCards = renderCategoryCards('LUNCH')
    const dinnerCards = renderCategoryCards('DINNER')
    const snackCards = renderCategoryCards('SNACK')
    const motionCards = renderMotionCards()

    if (
      breakfastCards === null &&
      lunchCards === null &&
      dinnerCards === null &&
      snackCards === null &&
      motionCards === null
    ) {
      return (
        <InfoPlaceholder text="Es gibt noch keinen Eintrag im Tagebuch und keinen Menüplan, fügen Sie jetzt über die Suche ein Nahrungsmittel, ein Rezept oder eine Aktivität hinzu. Dazu klicken Sie in das Suchfeld über dem Dashboard und suchen die einzelnen Nahrungsmittel oder Rezepte und fügen diese über «Hinzufügen» und «Speichern» zu Ihrem Tagebuch hinzu." />
      )
    }

    return (
      <>
        {breakfastCards}
        {lunchCards}
        {dinnerCards}
        {snackCards}
        {motionCards}
      </>
    )
  }

  const isRightColumnEmpty = macronutrientsHidden && hydrationHidden && vegetablesAndFruitsHidden && notesHidden

  if (isRightColumnEmpty) {
    return (
      <div className="container max-700">
        <JournalHeader />

        <Cockpit {...cockpitProps} />

        {renderJournalCards()}

        {renderRecipeModal()}

        {renderFoodModal()}
      </div>
    )
  }

  return (
    <div className="container max-900">
      <JournalHeader />

      <div className="row">
        <div className="col-12 col-lg-7">
          <Cockpit {...cockpitProps} />

          <JournalActions />

          {renderJournalCards()}
        </div>

        <div className="col-12 col-lg-5">
          {!macronutrientsHidden && (
            <MacronutrientCard
              isLoading={cockpitProps.isLoading}
              acceptedNutritions={cockpitProps.acceptedNutritions}
              goalNutritions={cockpitProps.goalNutritions}
            />
          )}

          {!hydrationHidden && <JournalCardHydrationContainer key={uniqueId()} />}
          {!vegetablesAndFruitsHidden && (
            <JournalCardVegetableAndFruitTracker
              flattenedJournalEatableRecordsContainers={flattenedJournalEatableRecordsContainers}
            />
          )}

          {!notesHidden && <JournalCardNotes />}
        </div>
      </div>

      {renderRecipeModal()}

      {renderFoodModal()}
    </div>
  )
}

export default JournalDay
